import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import {
  getScrollTop,
  scrollTo,
  getIntersectionRoot,
  getInfinityScrollRootMargin,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [infiniteLoader] - The infinite scroll loading indicator.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {Map<number, Promise<void>>} */
  #fetchPromises = new Map();

  /** @type {boolean} */
  #renderingNext = false;

  /** @type {boolean} */
  #renderingPrevious = false;

  /** @type {number} */
  #loadingCount = 0;

  /** @type {Set<string>} */
  #seenProductIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#indexExistingCards();
    this.#prefetchAroundCurrentPages();
    this.#observeViewMore();

    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Desktop (≥990px) scrolls `.page-wrapper`; mobile scrolls the document.
   * Recreate the observer whenever that root changes.
   */
  #handleBreakpointChange = () => {
    this.#observeViewMore();
  };

  #indexExistingCards() {
    this.#seenProductIds.clear();
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    for (const card of cards) {
      const id = card.dataset.productId;
      if (id) this.#seenProductIds.add(id);
    }
  }

  #prefetchAroundCurrentPages() {
    this.#fetchPage('next');
    this.#fetchPage('previous');
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    // Always recreate: the scroll root flips at the squeeze breakpoint, and
    // filter morphs replace sentinel nodes that still need observing.
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const { viewMorePrevious, viewMoreNext } = this.refs;

          if (entry.target === viewMorePrevious) {
            this.#renderPreviousPage();
          } else if (entry.target === viewMoreNext) {
            this.#renderNextPage();
          }
        }
      },
      {
        // On desktop the page scrolls inside `.page-wrapper`, not the viewport.
        // Without this root, sentinels never (reliably) intersect while scrolling.
        root: getIntersectionRoot(),
        // Start loading well before the sentinel reaches the visible area so
        // the next page is ready with no pause at the bottom of the grid.
        // Must use px/% — vh is invalid for IntersectionObserver rootMargin.
        rootMargin: getInfinityScrollRootMargin(),
        threshold: 0,
      }
    );

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Re-arm sentinels after a page append so IntersectionObserver fires again
   * when they are still inside the preload zone (isIntersecting never flipped).
   */
  #reobserveSentinels() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!this.infinityScrollObserver) return;

    if (viewMorePrevious) {
      this.infinityScrollObserver.unobserve(viewMorePrevious);
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;

    await this.#fetchSpecificPage(page.page, page.url);

    if (type === 'next') {
      const following = this.#pageInfoForNumber(page.page + 1);
      if (following && this.#shouldUsePage(following)) {
        this.#fetchSpecificPage(following.page, following.url);
      }
    }
  }

  /**
   * @param {number} pageNumber
   * @returns {{ page: number, url: URL }}
   */
  #pageInfoForNumber(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';
    return { page: pageNumber, url };
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return;

    const inflight = this.#fetchPromises.get(pageNumber);
    if (inflight) {
      await inflight;
      return;
    }

    const pageInfo = url ? { page: pageNumber, url } : this.#pageInfoForNumber(pageNumber);

    if (!this.#shouldUsePage(pageInfo)) return;

    const load = (async () => {
      this.#setLoading(true);
      try {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);
        this.pages.set(pageNumber, pageContent);
      } finally {
        this.#setLoading(false);
      }
    })();

    this.#fetchPromises.set(pageNumber, load);

    try {
      await load;
    } finally {
      this.#fetchPromises.delete(pageNumber);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.#loadingCount += isLoading ? 1 : -1;
    if (this.#loadingCount < 0) this.#loadingCount = 0;

    const active = this.#loadingCount > 0;
    this.toggleAttribute('loading', active);
    this.setAttribute('aria-busy', String(active));

    const loader = this.refs.infiniteLoader;
    if (!loader) return;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const current = this.#getPage('next');
    const hasMore = Boolean(current && current.page <= lastPage);

    loader.hidden = !active || !hasMore;
    loader.setAttribute('aria-hidden', String(loader.hidden));
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;

    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#renderingNext = true;
    let appended = false;

    try {
      if (!this.pages.has(nextPage.page)) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      }

      const nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements?.length) return;

      const fragment = document.createDocumentFragment();
      for (const node of nextPageItemElements) {
        if (!(node instanceof HTMLElement)) continue;
        const productId = node.dataset.productId;
        if (productId && this.#seenProductIds.has(productId)) continue;
        if (productId) this.#seenProductIds.add(productId);
        fragment.append(node);
      }

      if (!fragment.childNodes.length) return;

      grid.append(fragment);
      appended = true;

      this.#aspectRatioHelper?.processNewElements();

      history.replaceState('', '', nextPage.url.toString());

      this.#fetchPage('next');
    } finally {
      this.#renderingNext = false;
      // Sentinel often stays inside the preload margin after append —
      // without re-observe, no further pages load until it leaves and re-enters.
      if (appended) this.#reobserveSentinels();
    }
  }

  async #renderPreviousPage() {
    if (this.#renderingPrevious) return;

    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.#renderingPrevious = true;
    let prepended = false;

    try {
      if (!this.pages.has(previousPage.page)) {
        await this.#fetchSpecificPage(previousPage.page, previousPage.url);
      }

      const previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements?.length) return;

      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

      const fragment = document.createDocumentFragment();
      for (const node of previousPageItemElements) {
        if (!(node instanceof HTMLElement)) continue;
        const productId = node.dataset.productId;
        if (productId && this.#seenProductIds.has(productId)) continue;
        if (productId) this.#seenProductIds.add(productId);
        fragment.append(node);
      }

      if (!fragment.childNodes.length) return;

      grid.prepend(fragment);
      prepended = true;

      this.#aspectRatioHelper?.processNewElements();

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        scrollTo({
          top: currentScrollTop + (newHeight - oldHeight),
          behavior: 'instant',
        });
      }

      history.replaceState('', '', previousPage.url.toString());

      this.#fetchPage('previous');
    } finally {
      this.#renderingPrevious = false;
      if (prepended) this.#reobserveSentinels();
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    return this.#pageInfoForNumber(page);
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return [...gridElement.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#fetchPromises.clear();
    this.#seenProductIds.clear();
    this.#renderingNext = false;
    this.#renderingPrevious = false;
    this.#loadingCount = 0;
    this.removeAttribute('loading');
    this.setAttribute('aria-busy', 'false');
    sectionRenderer.clearCache(this.sectionId);
    const loader = this.refs.infiniteLoader;
    if (loader) {
      loader.hidden = true;
      loader.setAttribute('aria-hidden', 'true');
    }

    const observer = new MutationObserver(() => {
      observer.disconnect();

      if (!this.isConnected) return;

      this.#indexExistingCards();
      this.#observeViewMore();
      this.#prefetchAroundCurrentPages();
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });
    }
  };
}
